import {
  ControlBoardPlanLine,
  ControlBoardRow,
  ControlBoardView,
} from "../../types/controlBoard";
import { groupBy } from "./controlBoardModel";

/**
 * Lo que comparten las pestañas: las filas, el plan y cómo casar uno con otro. El plan va por
 * empresa (cada cliente + marca del BP se empareja con una), y el cliente del tablero es la
 * cuenta del registro comercial, así que se pasa por el mapa empresa → cliente que dan las
 * filas.
 */
export interface BoardData {
  year: number;
  lastMonth: number;
  dataUntil: string;
  rows: ControlBoardRow[];
  plan: ControlBoardPlanLine[];
  hasPlan: boolean;
  /** Líneas del plan sin empresa emparejada: no entran en las comparaciones por cliente. */
  unmappedPlan: ControlBoardPlanLine[];
  clientOf: (companyId: string | null, fallbackName: string) => string;
  clientName: (clientId: string) => string;
  /** Clientes ordenados por facturación real. */
  clients: { id: string; name: string }[];
  /** Plan de unas empresas. */
  planFor: (companyIds: Set<string>) => ControlBoardPlanLine[];
}

export function buildBoardData(view: ControlBoardView): BoardData {
  const companyToClient = new Map<string, string>();
  const names = new Map<string, string>();
  view.rows.forEach((row) => {
    companyToClient.set(row.companyId, row.clientId);
    names.set(row.clientId, row.client);
  });

  const clientOf = (companyId: string | null, fallbackName: string) => {
    if (companyId && companyToClient.has(companyId))
      return companyToClient.get(companyId) as string;
    const key = companyId ? `company:${companyId}` : `bp:${fallbackName}`;
    if (!names.has(key)) names.set(key, fallbackName);
    return key;
  };
  view.plan.forEach((line) =>
    clientOf(line.companyId, line.companyName || line.clientName)
  );

  const saleByClient = new Map<string, number>();
  view.rows.forEach((row) =>
    saleByClient.set(
      row.clientId,
      (saleByClient.get(row.clientId) ?? 0) + row.sale
    )
  );
  const clients = [...groupBy(view.rows, (r) => r.clientId).keys()]
    .map((id) => ({
      id,
      name: names.get(id) ?? id,
      sale: saleByClient.get(id) ?? 0,
    }))
    .sort((a, b) => b.sale - a.sale)
    .map(({ id, name }) => ({ id, name }));

  const mapped = view.plan.filter((line) => line.companyId);
  return {
    year: view.year,
    lastMonth: view.lastRealMonth,
    dataUntil: view.dataUntil,
    rows: view.rows,
    plan: mapped,
    hasPlan: view.plan.length > 0,
    unmappedPlan: view.plan.filter((line) => !line.companyId),
    clientOf,
    clientName: (id) => names.get(id) ?? id,
    clients,
    planFor: (companyIds) =>
      mapped.filter((line) => line.companyId && companyIds.has(line.companyId)),
  };
}

export interface TabProps {
  data: BoardData;
  /** Abre un cliente en la pestaña "Por cliente". */
  onOpenClient: (clientId: string) => void;
  /** Abre un account en la pestaña "Accounts". */
  onOpenAccount: (account: string) => void;
}

export const companyIdsOf = (rows: ControlBoardRow[]) =>
  new Set(rows.map((r) => r.companyId));
